import type { ComponentChildren } from "preact";
import { useMemo } from "preact/hooks";
import { ProviderComponentRuleList } from "@airvantage/ui";
import { COMPONENT_RULE_LIST } from "../constants/COMPONENT_RULE_LIST.js";
import VIEW_COMPONENT_LIST from "../components.js";
import { EMPTY_OBJECT } from "unchangeable";
import type { UserModel } from "../types";

type ProviderViewListProps = {
  children: ComponentChildren;
  /**
   * Optional context object for rule matching.
   * Can include user information, rights, or other contextual data.
   */
  context?: object;
  /**
   * Optional JSON-encoded user data string.
   * When provided, `userRights` is parsed from it and merged into the context.
   */
  user?: string;
};

/**
 * Provider wrapper for ViewList components.
 *
 * This component sets up the component resolution context for ViewList,
 * allowing views to use query-based component resolution via DynamicComponent.
 *
 * @example
 * ```tsx
 * <ProviderViewList user={userJson}>
 *   <ViewList value={viewList} onChange={setViewList} />
 * </ProviderViewList>
 * ```
 */
export function ProviderViewList({
  children,
  context = EMPTY_OBJECT,
  user,
}: ProviderViewListProps) {
  const viewContext = useMemo(() => {
    if (!user) {
      return context;
    }
    const userRights = (JSON.parse(user) as UserModel).rights;
    return { ...context, userRights };
  }, [context, user]);
  return (
    <ProviderComponentRuleList
      context={viewContext}
      value={COMPONENT_RULE_LIST}
      importModule={importModule}
    >
      {children}
    </ProviderComponentRuleList>
  );
}

async function importModule(componentModulePath: string) {
  const component = VIEW_COMPONENT_LIST.find(
    ({ path }) => path === componentModulePath
  );
  return component?.module();
}
